import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { Card, CardHeader, Button, Badge, Input, Spinner } from '@tkpod/ui';
import { api } from '@/lib/api';
import type { OptionTypeDto, CreateOptionTypeRequest, OptionUIType } from '@tkpod/shared';
import { AlertBanner } from '@/components/AlertBanner';
import { UI_TYPE_BADGE, UI_TYPE_OPTIONS } from '@/components/catalog/option-constants';
import { getDefaultSchemaForUiType } from '@/components/catalog/default-metadata-schema';

export function VariantOptionsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<OptionTypeDto | null>(null);

  const { data: optionTypes = [], isLoading } = useQuery<OptionTypeDto[]>({
    queryKey: ['option-types'],
    queryFn: () => api.get<OptionTypeDto[]>('/api/v1/admin/options/types'),
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Variant Options"
          description="Manage global option types (Color, Size, Material…) and their values."
          action={
            <div className="flex items-center gap-2">
              <Link to="/colors">
                <Button size="sm" variant="outline">
                  Color Library
                </Button>
              </Link>
              <Button size="sm" onClick={() => setShowCreateModal(true)}>
                Create Option Type
              </Button>
            </div>
          }
        />

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : optionTypes.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-sm text-text-tertiary">
            No option types found.
          </div>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-text-secondary">
                  <th className="px-6 py-3 font-medium">Internal Name</th>
                  <th className="px-6 py-3 font-medium">Display Name</th>
                  <th className="px-6 py-3 font-medium">UI Type</th>
                  <th className="px-6 py-3 font-medium">Values</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                  <th className="px-6 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {optionTypes.map((ot) => (
                  <tr key={ot.id} className="border-b border-border last:border-0 hover:bg-surface-secondary/50">
                    <td className="px-6 py-3">
                      <Link to={`/variant-options/${ot.id}`} className="font-medium text-brand-600 hover:underline">
                        {ot.name}
                      </Link>
                    </td>
                    <td className="px-6 py-3 text-text-secondary">
                      {ot.displayName || ot.name}
                    </td>
                    <td className="px-6 py-3">
                      <Badge variant={UI_TYPE_BADGE[ot.uiType] ?? 'default'} size="sm">
                        {ot.uiType.replace(/_/g, ' ')}
                      </Badge>
                    </td>
                    <td className="px-6 py-3">
                      <Badge variant="default" size="sm">{ot.valueCount} values</Badge>
                    </td>
                    <td className="px-6 py-3">
                      <Badge variant={ot.isActive ? 'success' : 'default'} size="sm">
                        {ot.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => navigate(`/variant-options/${ot.id}`)}>
                          View
                        </Button>
                        <button
                          type="button"
                          onClick={() => setDeleteDialog(ot)}
                          className="inline-flex items-center rounded-md border border-danger-300 bg-danger-50 px-2.5 py-1 text-xs font-medium text-danger-700 transition-colors hover:bg-danger-100"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showCreateModal && (
        <CreateOptionTypeModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={(id: string) => {
            queryClient.invalidateQueries({ queryKey: ['option-types'] });
            setShowCreateModal(false);
            navigate(`/variant-options/${id}`);
          }}
        />
      )}

      {deleteDialog && (
        <DeleteOptionTypeDialog
          optionType={deleteDialog}
          onClose={() => setDeleteDialog(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['option-types'] });
            setDeleteDialog(null);
          }}
        />
      )}
    </div>
  );
}

function CreateOptionTypeModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: (id: string) => void;
}) {
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [uiType, setUiType] = useState<OptionUIType>('DROPDOWN');
  const [sortOrder, setSortOrder] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const handleNameChange = (val: string) => {
    setName(val);
    if (!displayName || displayName === name) {
      setDisplayName(val);
    }
  };

  const createMutation = useMutation({
    mutationFn: (body: CreateOptionTypeRequest) =>
      api.post<OptionTypeDto>('/api/v1/admin/options/types', body),
    onSuccess: (res) => {
      onSuccess(res.id);
    },
    onError: (err: Error) => {
      setError(err.message || 'Failed to create option type.');
    },
  });

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !createMutation.isPending) onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [createMutation.isPending, onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim() || !displayName.trim()) {
      setError('Name and display name are required.');
      return;
    }
    if (name.trim().length > 100) {
      setError('Name must be at most 100 characters.');
      return;
    }
    createMutation.mutate({
      name: name.trim(),
      displayName: displayName.trim(),
      description: description.trim() || undefined,
      uiType,
      metadataSchema: getDefaultSchemaForUiType(uiType),
      sortOrder,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/40" onClick={() => !createMutation.isPending && onClose()} />
      <div className="relative z-10 w-full max-w-lg rounded-lg bg-surface p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-text-primary">Create Option Type</h3>
        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-text-secondary">Internal Name *</label>
            <Input value={name} onChange={(e) => handleNameChange(e.target.value)} placeholder="e.g. Comfort Colors Palette" maxLength={100} />
            <p className="mt-1 text-xs text-text-tertiary">
              Must be unique across all option types. Internal only — never shown to customers.
            </p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-text-secondary">Display Name *</label>
            <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="e.g. Color" />
            <p className="mt-1 text-xs text-text-tertiary">
              Shown on Shopify and in the designer. Can repeat — e.g. several color
              option types (one per brand) can all display as “Color”.
            </p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-text-secondary">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              rows={2}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-text-secondary">UI Type *</label>
            <select
              value={uiType}
              onChange={(e) => setUiType(e.target.value as OptionUIType)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            >
              {UI_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-text-secondary">Sort Order</label>
            <Input
              type="number"
              value={String(sortOrder)}
              onChange={(e) => setSortOrder(Number(e.target.value))}
            />
          </div>

          {error && <AlertBanner type="error" message={error} />}

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" size="sm" variant="outline" disabled={createMutation.isPending} onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteOptionTypeDialog({
  optionType,
  onClose,
  onSuccess,
}: {
  optionType: OptionTypeDto;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const isUsed = optionType.usedByTemplateCount > 0;

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/api/v1/admin/options/types/${optionType.id}`),
    onSuccess: () => onSuccess(),
    onError: (err: Error) => {
      setError(err.message || 'Failed to delete option type.');
    },
  });

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !deleteMutation.isPending) onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [deleteMutation.isPending, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/40" onClick={() => !deleteMutation.isPending && onClose()} />
      <div className="relative z-10 w-full max-w-md rounded-lg bg-surface p-6 shadow-xl">
        <div className="mb-1 flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-danger-100">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5 text-danger-600">
              <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
            </svg>
          </div>
          <h3 className="text-base font-semibold text-text-primary">Delete Option Type</h3>
        </div>

        {isUsed ? (
          <div className="mt-3 rounded-md border border-warning/30 bg-warning-light p-3 text-sm text-warning-dark">
            <strong>{optionType.name}</strong> is used by{' '}
            {optionType.usedByTemplateCount} product template{optionType.usedByTemplateCount > 1 ? 's' : ''}.
            Remove it from all templates before deleting.
          </div>
        ) : (
          <p className="mt-3 text-sm text-text-secondary">
            Are you sure you want to delete <strong className="text-text-primary">{optionType.name}</strong>?
            This will permanently remove the option type and all its values. This action cannot be undone.
          </p>
        )}

        {error && <div className="mt-3"><AlertBanner type="error" message={error} /></div>}

        <div className="mt-5 flex justify-end gap-3">
          <Button size="sm" variant="outline" disabled={deleteMutation.isPending} onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="danger"
            disabled={isUsed || deleteMutation.isPending}
            onClick={() => { setError(null); deleteMutation.mutate(); }}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Yes, Delete'}
          </Button>
        </div>
      </div>
    </div>
  );
}
